import { client } from '@blazeteams/sdk/client';
import { requireCurrentSession } from '@/features/auth/session-scope';
import { currentChatChannelsKey } from './creation-actions';
import { ChatMessageReadError } from './message-read-contract';
import * as React from 'react';
import {
  useQueryClient,
  useMutation,
  type InfiniteData,
} from '@tanstack/react-query';
import type { CreateChannelDto, SendMessageDto, UpdateChannelDto } from '@blazeteams/sdk';
import { useAuthStore } from '@/store/auth-store';
import { ownedChatQueries, useManagedChatCreation, currentChatChannel } from './creation-actions';
import { ownedChatMembers, useManagedChannelAction } from './management-actions';
import {
  getChannelNotifyPref,
  setChannelNotifyPref,
  serverChannelMuted,
  type ChannelNotifyPref,
} from './channel-notify-prefs';

import { selectFailedMessages, removeFailed, useFailedOutboxStore } from './failed-outbox';
import { captureChatOwner } from './creation-actions';
import { trackingRead } from './tracking-reads';
export { useUpsertThreadTracking, useRemoveThreadTracking, useCreateWorkThread } from './tracking-actions';
import { ownedMessageQueries } from './message-reads';
import { useOwnedMessageWrite } from './message-actions';
export { patchChannelMessagesCache } from './message-cache';
export { CHANNEL_MESSAGES_PAGE_SIZE } from './message-reads';
import type { ThreadTracking } from './thread-tracking';

// Local types — backend types are generic in the SDK
export interface ChannelMember {
  id?: string;
  channel_id?: string;
  joined_at?: string;
  user_id?: string;
  user_name?: string;
  user_avatar?: string;
  role?: string;
  last_read_at?: string;
  /** Q4: per-member mute flag (backend ChannelMemberDTO.is_muted). MY row's
   *  value is the server's mute state for this channel — resolved via
   *  `serverChannelMuted` (channel-notify-prefs.ts). */
  is_muted?: boolean;
}

// Backend returns one of: 'direct' (DM, 1:1), 'group' (multi-user channel),
// 'team' (department channel), 'customer' (legacy/special), or 'voice'
// (Discord-style persistent voice channel → a stable LiveKit room, see
// src/features/voice). The old UI's channels.service.ts is the source of truth
// for the text values; 'voice' was added with the voice-channels feature.
export type TeamChatChannelType = 'direct' | 'group' | 'team' | 'customer' | 'voice';

export interface Channel {
  id: string;
  tenant_id?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  avatar?: string;
  name: string;
  description?: string;
  type?: TeamChatChannelType | string;
  is_private?: boolean;
  /** Q7: per-viewer counterpart name for type='direct' channels, computed
   *  server-side in channelToDTO (omitted on non-direct). `dmDisplayName`
   *  prefers it above every client-side fallback. */
  display_name?: string;
  /** Q6: archived channels keep readable history but the backend rejects new
   *  messages (SendMessage → 409). Hidden from GET /channels unless
   *  `include_archived=true`. Toggled via useArchiveChannel/useUnarchiveChannel
   *  (owner/admin only — 403 otherwise). */
  is_archived?: boolean;
  /** Q4: NOT emitted top-level by the backend today (the flag lives on MY row
   *  in `members[]` — ChannelMemberDTO.is_muted); declared forward-compatible
   *  so a per-viewer scalar can land without an FE change. Always resolve via
   *  `serverChannelMuted(channel, myUserId)`, never read this directly. */
  is_muted?: boolean;
  member_count?: number;
  members?: ChannelMember[];
  last_message_preview?: string;
  last_message_sender?: string;
  last_message_at?: string;
  unread_count?: number;
}

// Native individual reaction rows become emoji/count/me for the captured viewer.
// No reactor names are invented at the HTTP boundary.
export interface MessageReaction {
  emoji: string;
  count?: number;
  me?: boolean;
  users?: Array<{ id?: string; name?: string }>;
}

/**
 * Provenance block stamped on a channel message that originated as a forward
 * from the customer Inbox (see `useForwardMessage` in the inbox feature). Null
 * on normal channel messages. Arrives both on the message-list refetch
 * (`GET .../messages`) and the realtime `message.new` broadcast — and since
 * the realtime path here invalidates+refetches (rather than reconstructing the
 * message from the socket payload), the field is never stripped: the channel's
 * "Reenviado de…" card just needs this on the type.
 */
export interface ForwardedFrom {
  source_message_id: string;
  source_conversation_id: string;
  contact_id?: string;
  /** Display name of the customer the message came from (falls back to phone/
   *  username server-side when there's no name). */
  contact_name: string;
  /** Origin channel provider, already humanized — "WhatsApp", "Messenger", … */
  provider: string;
  /** Original text/caption of the forwarded message. */
  original_content?: string;
  /** Original inbox message_type (image|document|text|audio|...). */
  original_message_type: string;
  forwarded_by_user_id: string;
  /** Display name of the agent who forwarded it. */
  forwarded_by_name: string;
  forwarded_at: string;
  /** Snapshot of the source conversation's last ~5 messages AT FORWARD TIME
   *  (chronological asc). Additive backend field (Matrix #91486, omitempty) —
   *  forwards created before it shipped simply don't carry it, so every
   *  consumer must tolerate `undefined`. Not live data: it never updates
   *  after the forward (the "Abrir conversación" deep-link is the live view). */
  last_messages?: Array<{
    direction?: 'inbound' | 'outbound' | string;
    content?: string;
    message_type?: string;
    created_at?: string;
  }>;
}

export interface ChatThreadPage {parent:ChannelMessage;replies:ChannelMessage[]}
export interface ChatThreadData {pages:ChatThreadPage[];pageParams:unknown[]}
export interface ChannelMessage {
  system_copy?: import('./system-copy').ChatSystemCopy;
  id?: string;
  channel_id?: string;
  user_id?: string;
  user_name?: string;
  user_avatar?: string;
  content?: string;
  created_at?: string;
  edited_at?: string;
  /** The backend's edit path sets `is_edited: true` (+ `updated_at`) rather
   *  than `edited_at` — confirmed by blazeteams-backend (Matrix #89694). The
   *  "(editado)" suffix must check BOTH fields: `edited_at` covers historical rows,
   *  `is_edited` covers the native current-server projection. */
  is_edited?: boolean;
  updated_at?: string;
  is_pinned?: boolean;
  reactions?: MessageReaction[];
  /** Set when this message is itself a reply in a thread. Top-level channel
   *  feed filters these out (backend does WHERE parent_message_id IS NULL),
   *  so we only see them inside `teamChatQueries.thread(...)`. */
  parent_message_id?: string;
  /** Inline count of replies on a message — backend joins it on parents in
   *  list responses. Used to render the ThreadIndicator. */
  reply_count?: number;
  /** Media fields — present when the message carries an attachment. The
   *  backend stores these from SendMessageDto (file_url + file_* metadata)
   *  and echoes them back on the message + the realtime `message.new`
   *  broadcast. `message_type` distinguishes 'text' from 'image'/'file'/etc. */
  message_type?: string;
  file_url?: string;
  file_name?: string;
  file_mime_type?: string;
  file_size?: number;
  /** Set when this message was forwarded from the customer Inbox — drives the
   *  "Reenviado de {contact} · {provider}" provenance card. */
  forwarded_from?: ForwardedFrom | null;
  /** Sensitive Inbox Agent tool request rendered as an approve/reject card. */
  agent_card?: import('@/features/inbox-agent/queries').AgentApprovalCardData | null;
  /** Owned HTTP projection of a root thread's tracking; absent/null when untracked. */
  tracking?: ThreadTracking | null;
  /** Local outbox only: full draft retained while the captured POST is pending. */
  pending?: boolean;
  /** Local outbox only: definite failure allows deliberate retry; unknown does not. */
  failed?: boolean;
  uncertain?: boolean;
  mentions?: string[];
}

/**
 * A message-search result row (GET /search/messages). The SDK types the 200
 * response as an opaque `{ [key: string]: unknown }`, so the shape lives here
 * — it mirrors the message rows the backend returns (a subset of
 * `ChannelMessage`, always scoped to one channel).
 */
export interface SearchHit {
  id: string;
  channel_id: string;
  user_id: string;
  user_name?: string;
  content: string;
  created_at: string;
}

/**
 * Map a MIME type to the team-chat `message_type` enum the backend accepts.
 * NOTE: team-chat uses `'file'` for the catch-all (the inbox uses
 * `'document'`) — different enums, so this can't share inbox's mapper.
 */
export function mimeToChannelMessageType(
  mime: string,
): SendMessageDto['message_type'] {
  const lower = (mime ?? '').toLowerCase();
  if (lower.startsWith('image/')) return 'image';
  if (lower.startsWith('video/')) return 'video';
  if (lower.startsWith('audio/')) return 'audio';
  return 'file';
}

export interface DirectoryUser {
  id: string;
  name: string;
  email?: string;
  role?: string;
  avatar?: string;
  presence?: string;
  lastSeen?: string;
}

export type ChannelMessagesInfiniteData = InfiniteData<ChannelMessage[], string | null>;

export function flattenMessagePages(
  data: ChannelMessagesInfiniteData | undefined,
): ChannelMessage[] {
  if (!data?.pages?.length) return [];
  const seen = new Set<string>();
  const out: ChannelMessage[] = [];
  for (const page of data.pages) {
    for (const m of page ?? []) {
      if (m.id) {
        if (seen.has(m.id)) continue;
        seen.add(m.id);
      }
      out.push(m);
    }
  }
  return out;
}

export const teamChatQueries = {
  /**
   * The caller's channel list. Q6: pass `{ includeArchived: true }` to also
   * get archived channels (backend: GET /channels?include_archived=true,
   * QueryBool — default false hides them).
   *
   * Owner/revision keys remain children of ['team-chat','channels'] so native
   * event invalidations cover both active and archived reads. Exact cache
   * consumers must select the current owner instead of the old bare key.
   */
  channels: (opts?: { includeArchived?: boolean }) => ownedChatQueries.channels(!!opts?.includeArchived),
  ...ownedMessageQueries,
  directory: () => ownedChatQueries.directory(),
  members: (channelId: string) => ownedChatMembers(channelId),
  /** Transitional legacy tracked lists: current owner/revision keys and native
   * owned directory checks. Backend tracking receipts are still pending. */
  channelThreads: (channelId:string,filters?:{status?:string;assigneeId?:string})=>trackingRead(channelId,filters),
  myTrackedThreads: (options?:{filter?:'mine'|'created'|'all';due?:'overdue'|'soon'})=>trackingRead('',undefined,options),
};

export function useMarkChannelRead() {
  const qc=useQueryClient();
  type Intent={owner:ReturnType<typeof captureChatOwner>;channelId:string;authorization:string};
  const mutation=useMutation({retry:false,gcTime:0,mutationFn:async(intent:Intent)=>{
    requireCurrentSession(intent.owner.revision);
    const result=await client.post<unknown>({url:'/channels/{id}/read',path:{id:intent.channelId},headers:{Authorization:intent.authorization,'Accept-Language':intent.owner.language}});
    requireCurrentSession(intent.owner.revision);
    if(result.error||result.response?.status!==200||!result.data||typeof result.data!=='object'||(result.data as {success?:boolean}).success!==true)throw new ChatMessageReadError();
  },onSuccess:(_result,intent)=>{requireCurrentSession(intent.owner.revision);void qc.invalidateQueries({queryKey:currentChatChannelsKey()});}});
  const capture=(channelId:string):Intent=>{const owner=captureChatOwner(),token=useAuthStore.getState().token;return {owner,channelId,authorization:token?.startsWith('Bearer ')?token:'Bearer '+(token??'')};};
  return {...mutation,mutate:(channelId:string)=>mutation.mutate(capture(channelId)),mutateAsync:(channelId:string)=>mutation.mutateAsync(capture(channelId))};
}

// Create a channel or DM. For DMs, pass type='direct' + member_ids=[otherUserId];
// the backend will return the existing direct channel if one already exists for
// the (caller, otherUserId) pair, so we don't have to dedupe on the client.
//
// `type: 'voice'` (Discord-style voice channel) is accepted by the backend
// (validated server-side) but the vendored SDK's CreateChannelDto union doesn't
// list it yet — so we widen the input type here and cast through at the
// postChannels boundary (same SDK-lies pattern as ChannelConfig).
export type CreateChannelInput = Omit<CreateChannelDto, 'type'> & {
  type: CreateChannelDto['type'] | 'voice';
};

export function useCreateChannel() {
  return useManagedChatCreation();
}

// ---------------------------------------------------------------------------
// Channel administration (rename / delete / members).
//
// Used by the voice-channel admin panel, but these are generic channel ops — a
// voice channel is a normal Channel, so the same CRUD applies (backend #44500).
// IMPORTANT: the rename body is `UpdateChannelDto`, which has no `type` field —
// a voice channel therefore can NOT be accidentally converted to text. The
// backend enforces channel-admin (PUT/DELETE → 403 "Not channel admin"); the
// UI surfaces that as a friendly inline error rather than pre-gating.
// ---------------------------------------------------------------------------
export function useUpdateChannel() {
  return useManagedChannelAction('update',(input:{channelId:string;patch:UpdateChannelDto})=>input);
}

export function useDeleteChannel() {
  return useManagedChannelAction('delete',(channelId:string)=>({channelId}));
}

// ---------------------------------------------------------------------------
// Q4 (TC-22): per-user channel mute — POST /channels/:id/mute | /unmute.
//
// Backend (chat_routes.go + channel_handler.setChannelMute): flips the
// CALLER's ChannelMember.is_muted (idempotent; 404 "Membership" when not a
// member). Response: { success, message, data: { is_muted } }. Not in the
// vendored SDK → raw shared sdkClient (auth/baseUrl already wired), same
// pattern as inbox's myPermissions.
//
// The mute state comes back on the channel list via members[].is_muted
// (my row), so invalidating ['team-chat','channels'] refreshes both the
// default and the {archived:true} variant (prefix match).
// ---------------------------------------------------------------------------
export function useMuteChannel() {
  return useManagedChannelAction('mute',(channelId:string)=>({channelId}));
}

export function useUnmuteChannel() {
  return useManagedChannelAction('unmute',(channelId:string)=>({channelId}));
}

/**
 * Q4 bridge: set a channel's 3-state notify pref, keeping the server in sync
 * on the 'none' boundary. This is THE write path for the pref — components
 * should call this instead of raw `setChannelNotifyPref` so the server mute
 * can't drift:
 *
 *   → 'none'            : persist locally + POST /mute (unless the cache
 *                          already shows the server muted).
 *   → 'all' | 'mentions': persist locally + POST /unmute when leaving 'none'
 *                          or when the cache shows a server mute (covers a
 *                          mute set from another device). 'mentions' itself
 *                          stays local — the server has no such level.
 *
 * Both endpoints are idempotent server-side, so a redundant call is harmless;
 * the cache checks just avoid pointless requests.
 */
export function useSetChannelNotifyPref() {
  const muteChannel = useMuteChannel();
  const unmuteChannel = useUnmuteChannel();
  const { mutate: mute } = muteChannel;
  const { mutate: unmute } = unmuteChannel;
  const qc = useQueryClient();

  return React.useCallback(
    (channelId: string, pref: ChannelNotifyPref) => {
      if (!channelId) return;
      const prevLocal = getChannelNotifyPref(channelId);
      setChannelNotifyPref(channelId, pref);

      const channel = currentChatChannel(qc, channelId);
      const serverMuted = serverChannelMuted(channel, useAuthStore.getState().user?.id);

      // If the server sync fails, roll the LOCAL pref back to its previous
      // value — otherwise localStorage keeps the new pref while the server
      // kept the old mute state, and effectiveNotifyPref drifts silently
      // whenever the server state is indeterminate (Copilot PR#6 review).
      const rollback = { onError: () => setChannelNotifyPref(channelId, prevLocal) };

      if (pref === 'none') {
        if (serverMuted !== true) mute(channelId, rollback);
      } else if (prevLocal === 'none' || serverMuted === true) {
        unmute(channelId, rollback);
      }
    },
    [mute, unmute, qc],
  );
}

// ---------------------------------------------------------------------------
// Q6 (TC-34): archive / unarchive — POST /channels/:id/archive | /unarchive.
//
// Backend (channel_handler.setChannelArchived): owner/admin only (403
// otherwise), idempotent, responds { success, message, data: <ChannelDTO> }
// and broadcasts `channel.updated` (which the realtime manager already maps
// to a ['team-chat','channels'] invalidation — the onSuccess below just
// closes the actor's latency gap). An archived channel keeps readable
// history but SendMessage rejects with 409 "Cannot send messages to an
// archived channel", and GET /channels hides it unless include_archived=true
// (see teamChatQueries.channels). Not in the vendored SDK → raw sdkClient.
// ---------------------------------------------------------------------------
export function useArchiveChannel() {
  return useManagedChannelAction('archive',(channelId:string)=>({channelId}));
}

export function useUnarchiveChannel() {
  return useManagedChannelAction('unarchive',(channelId:string)=>({channelId}));
}

export function useAddChannelMembers() {
  return useManagedChannelAction('add_members',(input:{channelId:string;userIds:string[]})=>input);
}

export function useRemoveChannelMember() {
  return useManagedChannelAction('remove_member',(input:{channelId:string;userId:string})=>input);
}

/** Variables `useSendChannelMessage` takes — exported so retry helpers and
 *  the upload hook can share the shape. */
export interface SendChannelMessageVars {
  channelId: string;
  content: string;
  parentMessageId?: string;
  /** When set, the message carries a media attachment. `messageType`
   *  should be derived via `mimeToChannelMessageType`. */
  fileUrl?: string;
  fileMimeType?: string;
  fileName?: string;
  fileSize?: number;
  messageType?: SendMessageDto['message_type'];
  mentions?: string[];
}

// Send message in internal channel via POST /messages (channel_id in body, not path)
//
// Pending, failed and uncertain literal drafts live in the owned memory outbox.
export function useSendChannelMessage() {
  return useOwnedMessageWrite((v: SendChannelMessageVars) => ({operation:'send',channelId:v.channelId,parentMessageId:v.parentMessageId,body:{
    channel_id:v.channelId,content:v.content,message_type:v.messageType??'text',
    ...(v.parentMessageId!==undefined?{parent_message_id:v.parentMessageId}:{}),
    ...(v.fileUrl!==undefined?{file_url:v.fileUrl}:{}),...(v.fileName!==undefined?{file_name:v.fileName}:{}),
    ...(v.fileMimeType!==undefined?{file_mime_type:v.fileMimeType}:{}),...(v.fileSize!==undefined?{file_size:v.fileSize}:{}),
    ...(v.mentions!==undefined?{mentions:v.mentions}:{})}}));
}
export function useRetryChannelMessage() {
  const send=useSendChannelMessage();
  const retry=(channelId:string,row:ChannelMessage)=>{
    const owner=captureChatOwner(), stored=selectFailedMessages(useFailedOutboxStore.getState(),channelId,owner).find(m=>m.id===row.id);
    if(!stored||!stored.failed||stored.uncertain||!stored.id)return;
    removeFailed(channelId,stored.id,owner);
    send.mutate({channelId,content:stored.content??'',parentMessageId:stored.parent_message_id,
      messageType:stored.message_type as SendMessageDto['message_type'],fileUrl:stored.file_url,fileName:stored.file_name,
      fileMimeType:stored.file_mime_type,fileSize:stored.file_size,mentions:stored.mentions});
  };
  return {retry,discard:(channelId:string,id:string)=>removeFailed(channelId,id),isPending:send.isPending};
}
export function useEditMessage(){return useOwnedMessageWrite((v:{channelId:string;messageId:string;content:string})=>({...v,operation:'edit',body:{content:v.content}}));}
export function useDeleteMessage(){return useOwnedMessageWrite((v:{channelId:string;messageId:string})=>({...v,operation:'delete'}));}
export function useTogglePin(){return useOwnedMessageWrite((v:{channelId:string;messageId:string;pinned:boolean})=>({channelId:v.channelId,messageId:v.messageId,operation:v.pinned?'unpin':'pin'}));}
export function useToggleReaction(){return useOwnedMessageWrite((v:{channelId:string;messageId:string;emoji:string;reacted:boolean;threadParentId?:string})=>({channelId:v.channelId,messageId:v.messageId,
  parentMessageId:v.threadParentId,operation:v.reacted?'unreact':'react',...(v.reacted?{emoji:v.emoji}:{body:{emoji:v.emoji}})}));}

// ---------------------------------------------------------------------------
// Hilos con seguimiento (thread tracking) — overlay 1:1 sobre los hilos
// existentes (spec 2026-07-07-thread-tracking-design.md, enfoque A).
//
// // contrato propuesto #93497 — endpoints pedidos al backend vía Matrix; NO
// están en el SDK vendored → sdkClient crudo. Shapes tolerantes: el backend
// puede desviar al confirmar. Regla de empty-UUID del repo: claves vacías se
// OMITEN del body, nunca se manda "".
// ---------------------------------------------------------------------------

/** Body parcial de PUT /messages/:id/tracking (upsert idempotente). */
export interface ThreadTrackingPatch {
  title?: string;
  status?: string;
  priority?: string;
  assignee_id?: string | null;
  due_at?: string | null;
}

/** Vars del modal "Nuevo hilo de trabajo" (header del canal). */
export interface CreateWorkThreadVars {
  channelId: string;
  title: string;
  firstMessage: string;
  assigneeId?: string;
  dueAt?: string;
  priority?: string;
}
